
import { MetalPrice } from './types';

export interface ChartDataPoint {
  timestamp: number;
  date: string;
  gold?: number;
  silver?: number;
}

export const processAPIData = (rawData: any, type: 'spot' | 'fixing' | 'storico') => {
  if (!rawData) {
    throw new Error('Invalid API data: received null or undefined');
  }

  // API response can be wrapped in a 'data' property or be the top-level object
  const sourceData = rawData.data || rawData;

  if (type === 'spot' || type === 'fixing') {
    if (!sourceData.XAU || !sourceData.XAG) {
      throw new Error(`Invalid ${type} price data format. Expected XAU and XAG properties.`);
    }
    return {
      gold: {
        price: parseFloat(sourceData.XAU.prezzo || '0'),
        change: parseFloat(sourceData.XAU.variazione_g || '0'),
        changePercentage: parseFloat(sourceData.XAU.variazione_percentuale || '0'),
        timestamp: sourceData.XAU.data_ora || new Date().toISOString(),
      },
      silver: {
        price: parseFloat(sourceData.XAG.prezzo || '0'),
        change: parseFloat(sourceData.XAG.variazione_g || '0'),
        changePercentage: parseFloat(sourceData.XAG.variazione_percentuale || '0'),
        timestamp: sourceData.XAG.data_ora || new Date().toISOString(),
      },
    };
  } else { // 'storico'
    if (!Array.isArray(sourceData)) {
      throw new Error('Historical data should be an array');
    }
    const goldHistory = sourceData
      .filter((item: any) => item && item.XAU && !isNaN(parseFloat(item.XAU)))
      .map((item: any): Partial<MetalPrice> => ({
        price: parseFloat(item.XAU),
        timestamp: item.periodo || new Date().toISOString(),
        change: 0,
        changePercentage: 0,
      }));
      
    const silverHistory = sourceData
      .filter((item: any) => item && item.XAG && !isNaN(parseFloat(item.XAG)))
      .map((item: any): Partial<MetalPrice> => ({
        price: parseFloat(item.XAG),
        timestamp: item.periodo || new Date().toISOString(),
        change: 0,
        changePercentage: 0,
      }));
      
    return {
      goldHistory,
      silverHistory
    };
  }
};

export const processChartData = (
  goldHistory: Partial<MetalPrice>[],
  silverHistory: Partial<MetalPrice>[]
): ChartDataPoint[] => {
  if (!goldHistory?.length && !silverHistory?.length) {
    return [];
  }

  const dataMap = new Map<string, Partial<ChartDataPoint>>();

  const processHistory = (history: Partial<MetalPrice>[], metal: 'gold' | 'silver') => {
    history.forEach(point => {
      if (point.timestamp && point.price) {
        const date = new Date(point.timestamp);
        const dateKey = date.toISOString().split('T')[0]; // Group by day
        const entry = dataMap.get(dateKey) || { timestamp: date.getTime(), date: date.toLocaleDateString() };
        
        if (metal === 'gold') {
          entry.gold = point.price;
        } else {
          entry.silver = point.price;
        }
        
        dataMap.set(dateKey, entry);
      }
    });
  };

  processHistory(goldHistory, 'gold');
  processHistory(silverHistory, 'silver');
  
  const sortedData = Array.from(dataMap.values()).sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

  return sortedData.map(d => ({
    timestamp: d.timestamp || 0,
    date: d.date || '',
    gold: d.gold,
    silver: d.silver
  }));
};
